import './StudioView.scss';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import { push } from 'react-router-redux';
import routes from 'library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import queryString from 'query-string';
import { compose } from 'recompose';
import {
  Link,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  Typography,
  Avatar,
  Collapse,
  Button,
  SnackbarContent,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Notifications from '@material-ui/icons/Notifications';
import { withLocale } from '../../hocs/withLocale';
import { withPublicStudio } from '../ScheduleManagment/withPublicStudio';
import { withPublicSchedule } from '../ScheduleManagment/withPublicSchedule';
import { SingleCourseView } from './SingleCourseView';
import BookingForm from './BookingForm';
import BookingFormNoPin from './BookingFormNoPin';
import StudioContactWidget from '../../routes/Studio/StudioContactWidget';
import moment from 'moment';
import {Course, Studio, StudioClient, Teacher, User} from "../../types";


interface TeachersMap {
  [key: string]: Teacher;
}

interface BookingValues {
  [key: string]: any; // Define this more specifically based on your form values
}

interface BookingCourseViewProps {
  classes: any;
  translate: (key: string) => string;
  match: {
    params: {
      courseId: string;
    };
  };
  location: {
    search: string;
  };
  studio: Studio;
  owner?: User;
  repeatableEvents: Course[];
  teachersMap: TeachersMap;
  user: any;
  date: moment.Moment;
  domain: any;
  registerToClass: (studio: Studio, classId: string) => void;
  toStudioClient: (studioClient: StudioClient) => void;
  toClassSignUp: (link: string) => void;
  clientAttendance: (studio: Studio, groupId: string, attendanceId: string) => void;
}

interface BookingCourseViewState {
  course?: Course;
  owner?: User;
  alternativeCourses: Course[];
  bookingSuccess?: boolean;
  showPricingDetails?: boolean;
  teacher?: Teacher;
}

class BookingCourseView extends React.Component<BookingCourseViewProps, BookingCourseViewState> {
  constructor(props: BookingCourseViewProps) {
    super(props);
    const course = this.props.repeatableEvents.filter(
      (e) => e._id === this.props.match.params.courseId
    )[0];
    this.state = {
      course,
      owner: props.owner,
      alternativeCourses: this.props.repeatableEvents.filter(
        (e) =>
          e.billingTag === course.billingTag &&
          e._id !== this.props.match.params.courseId &&
          e.allowBookCourse
      ),
    };
  }

  componentWillMount() {
    this.loadOwnerInfo(this.props.studio);
  }

  loadOwnerInfo = (studio: Studio) => {
    if (!this.state.owner) {
      serviceLocator.userApi.getOwnerInfo(studio.owner_id).then((owner) => {
        this.setState({ owner });
      }, LayoutHelper.onErr);
    }
  };

  render() {
    const { classes, studio, match, translate } = this.props;
    const { owner } = this.state;
    if (!owner) {
      return LayoutHelper.renderLoader();
    }
    const Form = studio.bookClassInfoWithPin ? BookingForm : BookingFormNoPin;
    return (
      <main className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            {this.renderRepeatableClass(this.state.course)}
          </Grid>
          <Grid item xs={12} md={5}>
            {this.state.bookingSuccess ? (
              <SnackbarContent
                message={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: this.state.course?.emptySlotBookingNote || '',
                    }}
                  />
                }
              />
            ) : (
              <Form
                studio={studio}
                courseId={match.params.courseId}
                buttonTitle={translate('studio.course.book')}
                owner={owner}
                onSubmit={async (values: BookingValues) => {
                  try {
                    const courseUrl = window.location.href;
                    await serviceLocator.registrationApi.registerForCourse(
                      studio._id,
                      match.params.courseId,
                      values,
                      courseUrl
                    );
                    this.setState({ bookingSuccess: true });
                  } catch (e) {
                    LayoutHelper.onErr(e);
                  }
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            {this.renderAlternativeCourses()}
          </Grid>
        </Grid>
      </main>
    );
  }

  renderAlternativeCourses = () => {
    if (
      this.state.alternativeCourses &&
      this.state.alternativeCourses.length > 0
    ) {
      return (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography component="h5">
              {this.props.translate('manageRegistration.alternativeCourses')}
            </Typography>
          </Grid>

          {this.state.alternativeCourses.map((alternativeCourse) => {
            return this.renderAlternativeCourse(alternativeCourse);
          })}
        </Grid>
      );
    }
    return null;
  };

  renderAlternativeCourse = (course: Course) => {
    return (
      <Grid item xs={12} key={course._id}>
        <SingleCourseView
          course={course}
          className={this.props.classes.alternativeCourse}
          studio={this.props.studio}
          teachersMap={this.props.teachersMap}
        />
      </Grid>
    );
  };

  renderRepeatableClass = (course?: Course) => {
    if (course) {
      if (!course.private && course.allowBookCourse) {
        return this.renderCourseHeader(course);
      } else {
        return this.props.translate('manageRegistration.courseIsNotAvailable');
      }
    } else {
      return LayoutHelper.renderLoader();
    }
  };

  renderCourseHeader = (course: Course) => {
    let teacher = this.state.teacher;
    return (
      <Card>
        <CardHeader
          avatar={
            <Avatar
              src={this.props.studio.avatar}
              className={this.props.classes.avatar}
            ></Avatar>
          }
          title={
            <Fragment>
              <Link href={routes.getStudioLink(this.props.studio)}>
                {' '}
                {this.props.studio.name}
              </Link>
              <StudioContactWidget studio={this.props.studio} />
            </Fragment>
          }
          subheader={this.props.studio.address}
        ></CardHeader>
        <CardHeader
          avatar={
            <Avatar
              src={course.avatar || (teacher && teacher.avatar)}
              className={this.props.classes.bigAvatar}
            />
          }
          title={course.title}
          subheader={teacher && teacher.name}
        ></CardHeader>
        {this.renderCourseDates(course)}
        <CardContent>{this.renderCourseDescription(course)}</CardContent>
        <CardActions>
          <Button
            size="small"
            color="secondary"
            onClick={() => {
              this.setState({
                showPricingDetails: !this.state.showPricingDetails,
              });
            }}
          >
            <Notifications />
            {this.props.translate('studio.pricingDetails')}
          </Button>
        </CardActions>
        <Collapse in={this.state.showPricingDetails} timeout="auto">
          <CardContent>
            <div
              dangerouslySetInnerHTML={{ __html: this.props.studio.pricing }}
              className="text"
            />
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  renderCourseDates = (course: Course) => {
    if (course.start && course.end) {
      let start = moment.utc(course.start);
      let end = moment.utc(course.end);
      return (
        <CardHeader
          title={
            <Fragment>
              {start.format('DD MMM YYYY')} - {end.format('DD MMM YYYY')}
            </Fragment>
          }
        />
      );
    }
    return null;
  };

  renderCourseDescription = (course: Course) => {
    if (course.description) {
      return (
        <Typography component="div">
          <div dangerouslySetInnerHTML={{ __html: course.description }} />
        </Typography>
      );
    }
    return null;
  };
}

function mapStateToProps(state: any, ownProps: any) {
  let date = queryString.parse(ownProps.location.search).date as string;
  const domain = state.domain.domain;
  let dateMoment;
  if (!date) {
    dateMoment = moment.utc().add(1, 'days').startOf('day');
  } else {
    dateMoment = moment.utc(date, 'DD-MM-YYYY').startOf('day');
  }

  return {
    user: state.user,
    date: dateMoment,
    domain: domain,
  };
}

interface DispatchProps {
  registerToClass: (studio: Studio, classId: string) => void;
  toStudioClient: (studioClient: StudioClient) => void;
  toClassSignUp: (link: string) => void;
  clientAttendance: (studio: Studio, groupId: string, attendanceId: string) => void;
}

const mapDispatchToProps = (dispatch: any): DispatchProps => {
  return {
    registerToClass: (studio: Studio, classId: string) => {
      if (studio.domainName) {
        dispatch(
          push(`${routes.class()}`)
        );
      } else {
        dispatch(push(`${routes.classId()}`));
      }
    },
    toStudioClient: (studioClient: StudioClient) => {
      dispatch(
        push(
          routes.studioClientClasses({
            ownerId: studioClient.ownerId,
            studioClientId: studioClient._id,
          })
        )
      );
    },
    toClassSignUp: (link: string) => {
      dispatch(push(link));
    },
    clientAttendance: (studio: Studio, groupId: string, attendanceId: string) => {
      dispatch(
        push(
          `${routes.clientAttendance({
            domainName: studio.domainName,
            groupId: groupId,
            id: attendanceId,
          })}`
        )
      );
    },
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocale,
  withPublicStudio,
  withPublicSchedule,
  withStyles((theme: any) => ({
    typography: {
      padding: theme.spacing(2),
    },
    card: {
      display: 'flex',
    },
    media: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    cover: {
      width: 150,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    root: {
      flexGrow: 1,
      padding: theme.spacing(2),
    },
    schedule: {
      maxWidth: theme.spacing(170),
      margin: `0 auto`,
      padding: `0 ${theme.spacing(2)}px`,
    },
    rooms: {
      margin: theme.spacing(1),
      padding: `${theme.spacing(2)}px 0`,
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: theme.spacing(15),
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    coursesGrid: {
      paddingTop: theme.spacing(2),
    },
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    buttons: {
      '& > *': {
        flex: 1,
      },
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
    },
    studioCard: {},
    studioCover: {
      height: 0,
      paddingTop: '56.25%', // 16:9
    },
    bigAvatar: {
      width: 150,
      height: 150,
    },
    alternativeCourse: {
      width: '100%',
    },
  }))
)(BookingCourseView);
